import { Button } from '@/components/ui/button';
import oktaAuth from '@/features/auth/services';
import { QuoteResultsDisplay } from '@/features/cancellationQuote/components/QuoteResultsDisplay';
import type {
  CancelQuoteResult,
  CancelReason,
  GetCancelQuoteRequest,
} from '@/features/cancellationQuote/types';
import type { ContractDetail } from '@/features/contractSearch/types';
import {
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';

// Define the state we expect to receive for this route
export interface QuoteResultsRouteState {
  quoteResults: CancelQuoteResult[];
  submittedQuoteRequest: GetCancelQuoteRequest;
  selectedContracts: ContractDetail[];
  cancelReasons: CancelReason[];
}

export const Route = createFileRoute('/quote-results')({
  // We are not using validateSearch or search params here, relying on state.
  // If state is missing or invalid, the component should handle it gracefully.
  beforeLoad: async ({ location }) => {
    const isAuthenticated = await oktaAuth.isAuthenticated();
    if (!isAuthenticated) {
      oktaAuth.signInWithRedirect({ originalUri: location.href });
      throw redirect({
        to: '/',
      });
    }
    return {};
  },
  component: QuoteResultsPage,
  errorComponent: QuoteResultsErrorComponent,
});

function QuoteResultsErrorComponent({ error }: { error: Error }) {
  const navigate = useNavigate();
  return (
    <div className='container mx-auto p-4 text-center'>
      <h1 className='text-2xl font-bold text-destructive mb-4'>
        Error Loading Quote Results
      </h1>
      <p className='text-lg mb-2'>
        An unexpected error occurred while trying to display the quote results.
      </p>
      <p className='text-sm text-muted-foreground mb-4'>{error.message}</p>
      <Button onClick={() => navigate({ to: '/search' })}>
        Start New Search
      </Button>
    </div>
  );
}

function QuoteResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // It's crucial that the state is correctly passed and typed here.
  // If state can be undefined (e.g., direct navigation), handle it.
  const state = location.state as unknown as QuoteResultsRouteState | undefined;

  if (
    !state ||
    !state.quoteResults ||
    !state.submittedQuoteRequest ||
    !state.selectedContracts ||
    !state.cancelReasons
  ) {
    // This can happen if the user navigates directly to this URL or state is lost.
    return (
      <div role='alert' className='container mx-auto p-4 text-center'>
        <h1 className='text-xl font-semibold text-warning mb-4'>
          Quote information is missing.
        </h1>
        <p className='mb-4'>Please start a new search to request quotes.</p>
        <Button onClick={() => navigate({ to: '/search' })}>
          Start New Search
        </Button>
      </div>
    );
  }

  return (
    <QuoteResultsDisplay
      quoteResults={state.quoteResults}
      submittedQuoteRequest={state.submittedQuoteRequest}
      selectedContracts={state.selectedContracts}
      cancelReasons={state.cancelReasons}
    />
  );
}
